/**
 * HIPAA-Compliant Session Timeout Hook
 *
 * Implements automatic session timeout per HIPAA §164.312(a)(2)(iii) - Automatic Logoff
 * Healthcare standard: 15-minute inactivity timeout with 2-minute warning
 *
 * Features:
 * - Tracks user activity (mouse, keyboard, scroll, touch)
 * - Shows warning modal at 13 minutes of inactivity
 * - Forces logout at 15 minutes
 * - Resets timer on any user activity
 */

import { useState, useEffect, useCallback, useRef } from 'react'

// HIPAA-compliant timeout settings (in milliseconds)
const SESSION_TIMEOUT_MS = 15 * 60 * 1000     // 15 minutes - force logout
const WARNING_THRESHOLD_MS = 13 * 60 * 1000   // 13 minutes - show warning

interface UseSessionTimeoutOptions {
  onLogout: () => void
  enabled?: boolean
}

interface SessionTimeoutState {
  showWarning: boolean
  remainingSeconds: number
  resetTimer: () => void
}

export function useSessionTimeout({
  onLogout,
  enabled = true
}: UseSessionTimeoutOptions): SessionTimeoutState {
  const [showWarning, setShowWarning] = useState(false)
  const [remainingSeconds, setRemainingSeconds] = useState(120) // 2 minutes warning period

  const lastActivityRef = useRef<number>(Date.now())
  const warningIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const checkIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Reset the activity timer
  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now()
    setShowWarning(false)
    setRemainingSeconds(120)

    // Clear warning countdown if active
    if (warningIntervalRef.current) {
      clearInterval(warningIntervalRef.current)
      warningIntervalRef.current = null
    }
  }, [])

  // Handle user activity
  const handleActivity = useCallback(() => {
    if (!enabled) return
    resetTimer()
  }, [enabled, resetTimer])

  // Check for timeout
  const checkTimeout = useCallback(() => {
    if (!enabled) return

    const now = Date.now()
    const inactiveTime = now - lastActivityRef.current

    // Force logout after 15 minutes
    if (inactiveTime >= SESSION_TIMEOUT_MS) {
      console.log('[Session Timeout] HIPAA: Forcing logout after 15 minutes of inactivity')
      onLogout()
      return
    }

    // Show warning after 13 minutes
    if (inactiveTime >= WARNING_THRESHOLD_MS && !showWarning) {
      console.log('[Session Timeout] HIPAA: Showing warning - 2 minutes until logout')
      setShowWarning(true)

      // Start countdown
      warningIntervalRef.current = setInterval(() => {
        const remaining = Math.ceil((SESSION_TIMEOUT_MS - (Date.now() - lastActivityRef.current)) / 1000)
        setRemainingSeconds(Math.max(0, remaining))

        if (remaining <= 0) {
          onLogout()
        }
      }, 1000)
    }
  }, [enabled, onLogout, showWarning])

  // Restart the inactivity clock when the session becomes active. While
  // unauthenticated (enabled === false) the activity listeners below are off,
  // so lastActivityRef would still hold the page-load time — and any idle spent
  // on the login screen would otherwise force an immediate logout the moment
  // the user signs in. Keyed only on `enabled` (resetTimer is stable) so it
  // never resets the live 13→15 min warning countdown, and declared before the
  // listener effect so the fresh baseline is in place before its first check.
  useEffect(() => {
    if (enabled) resetTimer()
  }, [enabled, resetTimer])

  // Set up activity listeners and timeout check
  useEffect(() => {
    if (!enabled) return

    // Activity events to track
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click']

    // Add listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true })
    })

    // Check timeout every 30 seconds
    checkIntervalRef.current = setInterval(checkTimeout, 30000)

    // Initial check
    checkTimeout()

    return () => {
      // Cleanup listeners
      events.forEach(event => {
        document.removeEventListener(event, handleActivity)
      })

      // Clear intervals
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current)
      }
      if (warningIntervalRef.current) {
        clearInterval(warningIntervalRef.current)
      }
    }
  }, [enabled, handleActivity, checkTimeout])

  return {
    showWarning,
    remainingSeconds,
    resetTimer
  }
}


